/**
 * Social connect + publish client (V2) — talks to the provider-agnostic
 * `POST /v2-social` lambda. TikTok today; instagram/linkedin/youtube later reuse
 * this file unchanged (they're just new `provider` values).
 *
 * OAuth flow: startSocialConnect() stores a CSRF `state` + return path in
 * sessionStorage and redirects to the provider's consent page. The provider
 * redirects back to /connect/<provider>/callback (SocialCallbackPage), which
 * verifies `state` and finishes the exchange server-side.
 */
import { API_CONFIG } from '@/api/config';
import { getIdToken } from '@/auth/firebase';
import { endSessionForReauth, isAuthRequiredError } from '@/api/banInterceptor';

export type SocialProvider =
  | 'tiktok' | 'youtube' | 'instagram' | 'twitter' | 'linkedin' | 'facebook' | 'threads'
  // Announced on the Integrations page as "coming soon" (real icons, no backend yet):
  | 'pinterest' | 'reddit' | 'vk' | 'dailymotion' | 'discord' | 'bluesky' | 'googledrive';

// Providers with a live backend module + OAuth app. Each still needs its real
// developer-app credentials filled in the `v2-social` lambda (and, for most, app
// review) before a connect actually completes — but the connect flow is wired for
// all of them. Drop a provider from this list to hide it as "coming soon".
export const AVAILABLE_PROVIDERS: readonly SocialProvider[] = [
  'tiktok', 'youtube', 'instagram', 'threads', 'facebook', 'linkedin', 'twitter', 'googledrive',
];

export const isProviderAvailable = (p: SocialProvider): boolean => AVAILABLE_PROVIDERS.includes(p);

// Providers we can PUBLISH a finished video to (backend `publish` implemented).
// Connect works for more platforms than we can post to yet; only these show a
// "Post to …" action on the finished video.
export const PUBLISHABLE_PROVIDERS: readonly SocialProvider[] = ['tiktok', 'facebook', 'instagram', 'threads', 'linkedin', 'youtube', 'googledrive', 'twitter'];

export const isProviderPublishable = (p: SocialProvider): boolean => PUBLISHABLE_PROVIDERS.includes(p);

// Providers live for EVERYONE (no allowlist). Only platforms whose APIs are fully
// approved/self-serve belong here. YouTube + Google Drive cleared Google OAuth
// verification (2026-07-30). TikTok cleared the Content Posting audit (2026-08-01).
// Meta approved all FB/IG/Threads permissions on 2026-09-05 (app 997806323099597)
// → Facebook Pages, Instagram Reels and Threads publishing are public. Only X
// (publish not implemented) remains allowlist-only.
export const PUBLIC_PROVIDERS: readonly SocialProvider[] = [
  'linkedin', 'youtube', 'googledrive', 'tiktok', 'facebook', 'instagram', 'threads',
];

// Every platform on the Integrations page, live or coming soon — the denominator of
// the "Connected Accounts N/14" counter on the finished-video screen.
export const ALL_PROVIDERS: readonly SocialProvider[] = [
  'tiktok', 'youtube', 'instagram', 'threads', 'twitter', 'linkedin', 'facebook',
  'pinterest', 'reddit', 'vk', 'dailymotion', 'discord', 'bluesky', 'googledrive',
];

// Which text field each platform consumes when publishing:
//  • CAPTION_PROVIDERS use the DESCRIPTION/caption (FB description, IG caption, Threads text).
//  • TITLE_PROVIDERS use the VIDEO TITLE (YouTube title/description).
//  • TikTok collects its own caption + privacy/disclosure in the Direct Post dialog.
// TikTok is included so the AI caption composer shows on the share sheet — its
// caption PRESETS the TikTok Direct Post dialog (where the user can still edit it
// and pick privacy/disclosure). The dialog reads it via initialCaption.
export const CAPTION_PROVIDERS: readonly SocialProvider[] = ['facebook', 'instagram', 'threads', 'linkedin', 'twitter', 'tiktok'];
export const TITLE_PROVIDERS: readonly SocialProvider[] = ['youtube'];
export const usesCaption = (p: SocialProvider): boolean => CAPTION_PROVIDERS.includes(p);
export const usesTitle = (p: SocialProvider): boolean => TITLE_PROVIDERS.includes(p);

export interface SocialAccount {
  provider: string;
  display_name: string;
  username?: string;       // @handle where the platform exposes one (IG, TikTok)
  avatar_url: string;
  follower_count?: number;  // 0/undefined = unknown
  connected_at: number;
}

const STATE_KEY = 'vv_social_state';
const RETURN_KEY = 'vv_social_return';
const VERIFIER_KEY = 'vv_social_verifier';

// PKCE code_verifier (X/Twitter requires it; harmless for the rest). Generated per
// connect, kept in sessionStorage, sent as the challenge on start_auth (method
// 'plain' → challenge == verifier) and as the verifier on complete_auth.
const makeVerifier = (): string => {
  const bytes = new Uint8Array(48);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, 64);
};

const getUserId = (): string => localStorage.getItem('vivideo_user_id') || '';

// JSON headers plus the caller's Bearer credential (backend verifies the owner).
const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getIdToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

const callSocial = async (payload: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const res = await fetch(`${API_CONFIG.baseUrl}/v2-social`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (isAuthRequiredError(res.status, data)) void endSessionForReauth();
  if (!res.ok) throw new Error((data.error as string) || `v2-social ${res.status}`);
  return data;
};

export const socialCallbackUri = (provider: SocialProvider): string =>
  `${window.location.origin}/connect/${provider}/callback`;

/** Kick off the OAuth connect: stores state + where to come back to, then redirects. */
export const startSocialConnect = async (provider: SocialProvider, returnTo: string): Promise<void> => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const verifier = makeVerifier();
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const data = await callSocial({
    action: 'start_auth',
    provider,
    redirect_uri: socialCallbackUri(provider),
    state,
    code_challenge: verifier, // PKCE (plain); ignored by non-PKCE providers
  });
  window.location.href = data.auth_url as string;
};

/** Finish the OAuth connect on the callback page. Throws on CSRF/state mismatch. */
export const completeSocialConnect = async (
  provider: SocialProvider,
  code: string,
  state: string,
): Promise<{ display_name?: string }> => {
  const expected = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY) || '';
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!expected || state !== expected) throw new Error('state mismatch — please retry connecting');
  const data = await callSocial({
    action: 'complete_auth',
    provider,
    user_id: getUserId(),
    code,
    redirect_uri: socialCallbackUri(provider),
    code_verifier: verifier, // PKCE (plain); ignored by non-PKCE providers
  });
  return (data.account as { display_name?: string }) || {};
};

/** Where the user was when they clicked Connect (set by startSocialConnect). */
export const consumeSocialReturnPath = (): string => {
  const path = sessionStorage.getItem(RETURN_KEY) || '/';
  sessionStorage.removeItem(RETURN_KEY);
  return path;
};

export const fetchSocialAccounts = async (): Promise<SocialAccount[]> => {
  const data = await callSocial({ action: 'accounts', user_id: getUserId() });
  return (data.accounts as SocialAccount[]) || [];
};

export const disconnectSocial = async (provider: SocialProvider): Promise<void> => {
  await callSocial({ action: 'disconnect', provider, user_id: getUserId() });
};

/** TikTok Direct Post options the compliant post screen collects and sends as post_info. */
export interface TikTokPostInfo {
  caption: string;
  privacy_level: string;          // one of the creator's privacy_level_options (no default)
  disable_comment: boolean;
  disable_duet: boolean;
  disable_stitch: boolean;
  brand_organic_toggle: boolean;  // "Your brand"
  brand_content_toggle: boolean;  // "Branded content"
}

/** The connected creator's allowed posting options (from TikTok creator_info/query).
 *  TikTok requires these to be rendered dynamically and NOT pre-selected. */
export interface TikTokCreatorInfo {
  creator_nickname: string;
  creator_username: string;
  creator_avatar_url: string;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number;
}

/** Query the connected TikTok creator's posting options — call this before showing the
 *  Direct Post screen so privacy levels + interaction toggles reflect THIS creator. */
export const getTikTokCreatorInfo = async (): Promise<TikTokCreatorInfo> => {
  const data = await callSocial({ action: 'creator_info', provider: 'tiktok', user_id: getUserId() });
  return data as unknown as TikTokCreatorInfo;
};

/** Saved TikTok post preferences (the gear's "settings"). All options — including
 *  privacy_level — are saved here and applied on a one-tap post.
 *  COMPLIANCE NOTE: TikTok's Content Posting UX guidelines require the user to
 *  ACTIVELY select privacy_level on every post (no pre-selected/saved default). This
 *  design deliberately saves it and posts in one tap per product decision; be aware
 *  it may be flagged during TikTok's app audit. */
export interface TikTokDefaults {
  caption: string;
  privacy_level: string;
  disable_comment: boolean;
  disable_duet: boolean;
  disable_stitch: boolean;
  brand_organic_toggle: boolean;
  brand_content_toggle: boolean;
}

const TT_DEFAULTS_KEY = 'vv_tiktok_defaults';
const TT_DEFAULTS_FALLBACK: TikTokDefaults = {
  caption: '', privacy_level: 'PUBLIC_TO_EVERYONE',
  disable_comment: false, disable_duet: false, disable_stitch: false,
  brand_organic_toggle: false, brand_content_toggle: false,
};

export const loadTikTokDefaults = (): TikTokDefaults => {
  try {
    const raw = localStorage.getItem(TT_DEFAULTS_KEY);
    return raw ? { ...TT_DEFAULTS_FALLBACK, ...JSON.parse(raw) } : { ...TT_DEFAULTS_FALLBACK };
  } catch {
    return { ...TT_DEFAULTS_FALLBACK };
  }
};

export const saveTikTokDefaults = (d: TikTokDefaults): void => {
  try { localStorage.setItem(TT_DEFAULTS_KEY, JSON.stringify(d)); } catch { /* ignore */ }
};

/** Publish the finished video to the user's account. For TikTok this is a DIRECT POST
 *  and `postInfo` (privacy/interaction/disclosure) is required; for URL/caption
 *  platforms (FB/IG/Threads/LinkedIn/YouTube) pass a `caption`. Returns a publish_id
 *  to poll with fetchSocialPostStatus. */
export const postVideoToSocial = async (
  provider: SocialProvider,
  videoUrl: string,
  caption?: string,
  postInfo?: TikTokPostInfo,
): Promise<string> => {
  const data = await callSocial({
    action: 'post', provider, user_id: getUserId(), video_url: videoUrl,
    caption: postInfo?.caption ?? caption ?? '',
    ...(postInfo ? {
      privacy_level: postInfo.privacy_level,
      disable_comment: postInfo.disable_comment,
      disable_duet: postInfo.disable_duet,
      disable_stitch: postInfo.disable_stitch,
      brand_organic_toggle: postInfo.brand_organic_toggle,
      brand_content_toggle: postInfo.brand_content_toggle,
    } : {}),
  });
  return data.publish_id as string;
};

export const fetchSocialPostStatus = async (
  provider: SocialProvider,
  publishId: string,
): Promise<{ status: string; fail_reason?: string }> => {
  const data = await callSocial({ action: 'status', provider, user_id: getUserId(), publish_id: publishId });
  return data as { status: string; fail_reason?: string };
};
